import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { menu } from './Menu.tokens.js';
import { typographyTemplate, spacingsTemplate, outlineTemplate, mergeRefs } from '@equinor/eds-utils';
import { useMenu } from './Menu.context.js';
import { jsx } from 'react/jsx-runtime';

var _excluded = ["children", "disabled", "active", "index", "as", "onClick", "closeMenuOnClick"];
var typography = menu.typography,
  _tokens$entities = menu.entities,
  _tokens$entities$item = _tokens$entities.item.states,
  activeToken = _tokens$entities$item.active,
  focus = _tokens$entities$item.focus,
  hover = _tokens$entities$item.hover,
  disabledToken = _tokens$entities$item.disabled,
  icon = _tokens$entities.icon;
var Item = styled.button.attrs(function (_ref) {
  var $isFocused = _ref.$isFocused;
  return {
    role: 'menuitem',
    tabIndex: $isFocused ? -1 : 0
  };
}).withConfig({
  displayName: "MenuItem__Item",
  componentId: "sc-1g9fpbe-0"
})(["border:0;background-color:transparent;width:auto;position:relative;z-index:2;text-decoration:none;", " ", " ", " ", ""], typographyTemplate(typography), function (_ref2) {
  var theme = _ref2.theme;
  return spacingsTemplate(theme.entities.item.spacings);
}, function (_ref3) {
  var $active = _ref3.$active;
  return $active && css(["background:", ";*{color:", ";}"], activeToken.background, activeToken.typography.color);
}, function (_ref4) {
  var disabled = _ref4.disabled;
  return disabled ? css(["*{color:", ";}svg{fill:", ";}&:focus{outline:none;}@media (hover:hover) and (pointer:fine){&:hover{cursor:not-allowed;}}"], disabledToken.typography.color, icon.states.disabled.typography.color) : css(["@media (hover:hover) and (pointer:fine){&:hover{z-index:1;cursor:pointer;background:", ";}}&:focus{z-index:3;", "}"], hover.background, outlineTemplate(focus.outline));
});
var Content = styled.div.withConfig({
  displayName: "MenuItem__Content",
  componentId: "sc-1g9fpbe-1"
})(["width:auto;display:grid;grid-gap:16px;grid-auto-flow:column;grid-auto-columns:max-content auto max-content;align-items:center;"]);
var MenuItem = /*#__PURE__*/forwardRef(function MenuItem(_ref5, ref) {
  var children = _ref5.children,
    disabled = _ref5.disabled,
    active = _ref5.active,
    _ref5$index = _ref5.index,
    index = _ref5$index === void 0 ? 0 : _ref5$index,
    _ref5$as = _ref5.as,
    as = _ref5$as === void 0 ? 'button' : _ref5$as,
    _onClick = _ref5.onClick,
    _ref5$closeMenuOnClic = _ref5.closeMenuOnClick,
    closeMenuOnClick = _ref5$closeMenuOnClic === void 0 ? true : _ref5$closeMenuOnClic,
    rest = _objectWithoutProperties(_ref5, _excluded);
  var _useMenu = useMenu(),
    focusedIndex = _useMenu.focusedIndex,
    setFocusedIndex = _useMenu.setFocusedIndex,
    onClose = _useMenu.onClose;
  var toggleFocus = function toggleFocus(index_) {
    if (focusedIndex !== index_) {
      setFocusedIndex(index_);
    }
  };
  var isFocused = index === focusedIndex;
  var props = _objectSpread(_objectSpread({}, rest), {}, {
    as: as,
    disabled: disabled,
    $isFocused: isFocused
  });
  return /*#__PURE__*/jsx(Item, _objectSpread(_objectSpread({}, props), {}, {
    $active: active,
    ref: mergeRefs(ref, function (el) {
      if (isFocused) {
        requestAnimationFrame(function () {
          if (el !== null) el.focus();
        });
      }
    }),
    onFocus: function onFocus() {
      return toggleFocus(index);
    },
    onClick: function onClick(e) {
      if (_onClick) {
        _onClick(e);
        if (onClose !== null && closeMenuOnClick) {
          onClose(e);
        }
      }
    },
    children: /*#__PURE__*/jsx(Content, {
      children: children
    })
  }));
});
MenuItem.displayName = 'MenuItem';

export { MenuItem };
